import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'fr' | 'en';

interface Translations {
    [key: string]: string | Translations;
}

const translations: Record<Language, Translations> = {
    fr: {
        // Navigation
        dashboard: 'Dashboard',
        import: 'Import',
        export: 'Export',
        parametres: 'Paramètres',
        deconnexion: 'Déconnexion',
        connexion: 'Connexion',

        // Paramètres
        parametresGeneraux: 'Paramètres généraux',
        parametresNotifications: 'Paramètres de notifications',
        parametresAffichage: 'Paramètres d\'affichage',
        langueInterface: 'Langue de l\'interface',
        fuseauHoraire: 'Fuseau horaire',
        formatDate: 'Format de date',
        themeApplication: 'Thème de l\'application',
        clair: 'Clair',
        sombre: 'Sombre',
        enregistrer: 'Enregistrer les modifications',

        // Thème
        notificationsEmail: 'Notifications par email',
        notificationsApp: 'Notifications dans l\'application',
        typesAlertes: 'Types d\'alertes',

        // Messages
        succes: 'Succès',
        erreur: 'Erreur',
        chargement: 'Chargement...',
        sauvegardeReussie: 'Paramètres enregistrés avec succès',

        // Tableaux
        nombreElementsPage: 'Nombre d\'éléments par page',
        rafraichissementAuto: 'Rafraîchissement automatique',

        // Actions
        ajouter: 'Ajouter',
        modifier: 'Modifier',
        supprimer: 'Supprimer',
        annuler: 'Annuler',
        confirmer: 'Confirmer',
        retour: 'Retour',

        // Statuts
        actif: 'Actif',
        inactif: 'Inactif',
        enAttente: 'En attente',
        termine: 'Terminé',

        // Dossiers
        dossiersImport: 'Dossiers Import',
        dossiersExport: 'Dossiers Export',
        nouveauDossier: 'Nouveau Dossier',
        numeroDossier: 'Numéro de dossier',
        client: 'Client',
        transitaire: 'Transitaire',

        // Livraisons
        livraisons: 'Livraisons',
        recuperations: 'Récupérations',
        chauffeurs: 'Chauffeurs',
        camions: 'Camions',

        // Sidebar - Menu items
        gestionUtilisateurs: 'Gestion Utilisateurs',
        gestionParc: 'Gestion Parc',
        gestionChauffeurs: 'Gestion Chauffeurs',
        gestionCamions: 'Gestion Camions',
        gestionClients: 'Gestion Clients',
        gestionTransitaires: 'Gestion Transitaires',
        tracking: 'Tracking',
        facturations: 'Facturations',
        statistiques: 'Statistiques',
        exports: 'Exports',

        // Général
        recherche: 'Recherche',
        filtrer: 'Filtrer',
        voir: 'Voir',
        actions: 'Actions',
        date: 'Date',
        statut: 'Statut',
    },
    en: {
        // Navigation
        dashboard: 'Dashboard',
        import: 'Import',
        export: 'Export',
        parametres: 'Settings',
        deconnexion: 'Logout',
        connexion: 'Login',

        // Paramètres
        parametresGeneraux: 'General Settings',
        parametresNotifications: 'Notification Settings',
        parametresAffichage: 'Display Settings',
        langueInterface: 'Interface Language',
        fuseauHoraire: 'Time Zone',
        formatDate: 'Date Format',
        themeApplication: 'Application Theme',
        clair: 'Light',
        sombre: 'Dark',
        enregistrer: 'Save Changes',

        // Thème
        notificationsEmail: 'Email Notifications',
        notificationsApp: 'In-App Notifications',
        typesAlertes: 'Alert Types',

        // Messages
        succes: 'Success',
        erreur: 'Error',
        chargement: 'Loading...',
        sauvegardeReussie: 'Settings saved successfully',

        // Tableaux
        nombreElementsPage: 'Items per page',
        rafraichissementAuto: 'Auto refresh',

        // Actions
        ajouter: 'Add',
        modifier: 'Edit',
        supprimer: 'Delete',
        annuler: 'Cancel',
        confirmer: 'Confirm',
        retour: 'Back',

        // Statuts
        actif: 'Active',
        inactif: 'Inactive',
        enAttente: 'Pending',
        termine: 'Completed',

        // Dossiers
        dossiersImport: 'Import Files',
        dossiersExport: 'Export Files',
        nouveauDossier: 'New File',
        numeroDossier: 'File Number',
        client: 'Client',
        transitaire: 'Forwarder',

        // Livraisons
        livraisons: 'Deliveries',
        recuperations: 'Pickups',
        chauffeurs: 'Drivers',
        camions: 'Trucks',

        // Sidebar - Menu items
        gestionUtilisateurs: 'User Management',
        gestionParc: 'Park Management',
        gestionChauffeurs: 'Driver Management',
        gestionCamions: 'Truck Management',
        gestionClients: 'Client Management',
        gestionTransitaires: 'Forwarder Management',
        tracking: 'Tracking',
        facturations: 'Billing',
        statistiques: 'Statistics',
        exports: 'Exports',

        // Général
        recherche: 'Search',
        filtrer: 'Filter',
        voir: 'View',
        actions: 'Actions',
        date: 'Date',
        statut: 'Status',
    }
};

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        // Récupérer la langue depuis localStorage ou utiliser 'fr' par défaut
        const savedGeneral = localStorage.getItem('settings_general');
        if (savedGeneral) {
            try {
                const parsed = JSON.parse(savedGeneral);
                return parsed.langue === 'en' ? 'en' : 'fr';
            } catch {
                return 'fr';
            }
        }
        return 'fr';
    });

    const setLanguage = (newLanguage: Language) => {
        setLanguageState(newLanguage);
        // Mettre à jour localStorage
        const savedGeneral = localStorage.getItem('settings_general');
        const generalSettings = savedGeneral ? JSON.parse(savedGeneral) : {};
        generalSettings.langue = newLanguage;
        localStorage.setItem('settings_general', JSON.stringify(generalSettings));
        // Mettre à jour l'attribut lang du document
        document.documentElement.lang = newLanguage;
    };

    // Fonction de traduction
    const t = (key: string): string => {
        const keys = key.split('.');
        let value: any = translations[language];

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                return key; // Retourne la clé si la traduction n'existe pas
            }
        }

        return typeof value === 'string' ? value : key;
    };

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};
